/* eslint-disable @next/next/no-img-element */
import { useContext, useState } from 'react';
import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import { MPosContext } from 'utils/context';
import { useRouter } from 'next/router';
import {
    getPartnerContext,
    savePartnerContext,
} from 'utils/localStorage/partnerStorage';
import { useFetchParams } from 'hooks/useFetchParams';
import { useViewport } from 'utils/responsive';
import {
    NewPlanStages,
    policyDetailsInitState,
} from 'components/newPlan/constants';
import {
    SegmentEventNameTypes,
    SegmentEventActionTypes,
    SegmentEventButtonNames,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventActionNames,
} from 'utils/segment/constants';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import * as Styles from './styles';
import { AckoLogoConfig } from 'utils/constants';

function Header({ wrapperStyles = {} }) {
    const { lob } = useFetchParams();
    const router = useRouter();
    const {
        partnerId,
        permissions,
        showHamburger,
        setShowHamburger,
        setActiveStage,
        setPlanDetails,
        userAccessList,
    } = useContext(MPosContext);

    const isLoginPage = router.pathname.includes('login');
    const isLegalDocumentsPage = router.asPath.includes('/legal-documents');
    const isDealerDetailsPage = router.asPath.includes('/dealer-details');
    const isGstDetailsPage = router.asPath.includes('/gst-details');
    const isDetailsPage = router.asPath.endsWith('/details');
    const hideGroupSwitchOnPage =
        router.pathname === '/partners' ||
        router.pathname === '/partners/access';
    const { isMobile } = useViewport();
    const [showGroupDropdown, setShowGroupDropdown] = useState(false);

    const getPartnerLogo = () => {
        try {
            const { partner_logo } = permissions;
            if (partner_logo) {
                return partner_logo;
            }
            switch (partnerId) {
                case 'oppo':
                    return '/logos/oppo.png';
                case 'ather':
                    return '/logos/ather.svg';
                default:
                    return '';
            }
        } catch {
            return '';
        }
    };

    const disableLogoNavigation =
        isLoginPage ||
        isLegalDocumentsPage ||
        isDealerDetailsPage ||
        isGstDetailsPage ||
        isDetailsPage;

    const logoOnClick = () => {
        if (!disableLogoNavigation) {
            setShowHamburger(false);
            setActiveStage(NewPlanStages.MODEL);
            setPlanDetails(policyDetailsInitState);
            if (
                permissions.permission_list.includes(
                    'access_only_auto_commercial_flow'
                )
            ) {
                router.push(`/${lob}/${partnerId}/form`);
                return;
            }
            router.push(`/${lob}/${partnerId}/dashboard`);
        }
    };

    const onClickMenu = () => {
        setShowHamburger((s: boolean) => !s);

        /**
         * segment event
         */

        trackEvent(SegmentEventNameTypes.ACTION, {
            page_url: window?.location?.href || '',
            action_type: SegmentEventActionTypes.TAP_BTN,
            button_name: SegmentEventButtonNames.MENU,
            product: PRODUCT,
            sub_product: SUB_PRODUCT_NAME,
            platform: isMobile ? 'mweb' : 'web',
            device: navigator.userAgent || '',
            partner: partnerId,
            action_name: SegmentEventActionNames.CLICK_MENU,
        });
    };

    const partnerLogo = getPartnerLogo();
    const selectedPartnerContext = getPartnerContext();
    const currentPartnerId =
        selectedPartnerContext?.partner_id ||
        permissions?.partner_id ||
        partnerId;
    const currentPartnerAccess = userAccessList?.find(
        (access: any) =>
            (access?.partner_id || '').toLowerCase() ===
            (currentPartnerId || '').toLowerCase()
    );
    const groups = currentPartnerAccess?.groups || [];
    const hasMultipleGroups = groups.length > 1;
    const selectedGroupName = selectedPartnerContext?.group?.group_name;
    const selectedGroup =
        groups.find((group: any) => group.group_name === selectedGroupName) ||
        groups[0];
    const switchLabel = selectedGroup?.store_type || 'Switch';

    const onClickSwitchGroup = () => {
        setShowGroupDropdown((state) => !state);
    };

    const onSelectGroup = (group: any) => {
        if (!currentPartnerId) return;
        savePartnerContext(currentPartnerId, group);
        setShowGroupDropdown(false);
        setShowHamburger(false);
        router.reload();
    };

    return (
        <Styles.Wrapper style={wrapperStyles}>
            <Styles.LogosWrapper>
                <Styles.AckoLogoWrapper>
                    <Image
                        src={AckoLogoConfig.gradient}
                        width={'82px'}
                        height={'26px'}
                        onClick={logoOnClick}
                    />
                </Styles.AckoLogoWrapper>
                {!hideGroupSwitchOnPage && partnerLogo && (
                    <Styles.PartnerLogoWrapper>
                        <div
                            style={{
                                borderLeft: '1px solid #E7E7F0',
                                marginRight: '8px',
                            }}
                        ></div>
                        <div style={{ display: 'flex' }}>
                            <Image
                                src={partnerLogo}
                                width={'70px'}
                                height={'22px'}
                                alt={''}
                            />
                        </div>
                    </Styles.PartnerLogoWrapper>
                )}
            </Styles.LogosWrapper>
            {!isLoginPage && (
                <Styles.RightControls>
                    {!showHamburger &&
                        !hideGroupSwitchOnPage &&
                        hasMultipleGroups && (
                            <Styles.GroupSwitchWrapper>
                                <Styles.GroupSwitchButton
                                    type="button"
                                    onClick={onClickSwitchGroup}
                                >
                                    <Styles.GroupSwitchIcon>
                                        &#8646;
                                    </Styles.GroupSwitchIcon>
                                    <Styles.GroupSwitchText>
                                        {switchLabel}
                                    </Styles.GroupSwitchText>
                                </Styles.GroupSwitchButton>
                                {showGroupDropdown && (
                                    <Styles.GroupDropdown>
                                        {groups.map((group: any) => (
                                            <Styles.GroupDropdownItem
                                                key={group.group_name}
                                                type="button"
                                                isActive={
                                                    group.group_name ===
                                                    selectedGroupName
                                                }
                                                onClick={() =>
                                                    onSelectGroup(group)
                                                }
                                            >
                                                {group.store_type}
                                            </Styles.GroupDropdownItem>
                                        ))}
                                    </Styles.GroupDropdown>
                                )}
                            </Styles.GroupSwitchWrapper>
                        )}
                    <Styles.HamburgerWrapper>
                        <Image
                            style={{ cursor: 'pointer' }}
                            src={getImageBasePath(
                                showHamburger
                                    ? '/icons/close.svg'
                                    : '/icons/hamburger.svg'
                            )}
                            width={'24px'}
                            height={'24px'}
                            onClick={onClickMenu}
                        />
                    </Styles.HamburgerWrapper>
                </Styles.RightControls>
            )}
        </Styles.Wrapper>
    );
}

export default Header;
